import { Layout } from "@/components/layout/Layout";
import { usePageMeta } from "@/hooks/use-page-meta";
import { site } from "@/data/site";
import { Link } from "wouter";

const EFFECTIVE_DATE = "July 16, 2025";
const LAST_UPDATED = "July 16, 2026";

export default function Terms() {
  usePageMeta(
    "Terms of Use",
    "Terms of use for travisjohnson.xyz — rules governing access to and use of this site."
  );

  return (
    <Layout>
      <div className="container mx-auto px-6 max-w-3xl py-12 md:py-20">
        <h1 className="text-4xl md:text-5xl font-serif font-medium tracking-tight mb-4">
          Terms of Use
        </h1>
        <p className="text-sm text-muted-foreground mb-12">
          Effective: {EFFECTIVE_DATE} · Last updated: {LAST_UPDATED}
        </p>

        <div className="prose prose-lg dark:prose-invert max-w-none space-y-10">

          <section>
            <h2 className="text-2xl font-serif font-medium text-foreground mb-4 mt-0">1. Acceptance of Terms</h2>
            <p className="text-muted-foreground leading-relaxed">
              By accessing or using <strong>travisjohnson.xyz</strong> (the "Site"), you agree to be bound
              by these Terms of Use ("Terms"). If you do not agree to these Terms, please do not use the
              Site. These Terms apply to all visitors, users, and others who access the Site.
            </p>
            <p className="text-muted-foreground leading-relaxed">
              The Site is owned and operated by Travis Johnson, an individual based in {site.location}.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-serif font-medium text-foreground mb-4">2. Description of the Site</h2>
            <p className="text-muted-foreground leading-relaxed">
              This Site is a personal portfolio and professional reference for Travis Johnson. It includes:
            </p>
            <ul className="list-disc pl-6 text-muted-foreground space-y-1 mt-2">
              <li>Portfolio and work history information;</li>
              <li>Field notes and editorial writing;</li>
              <li>A contact form for professional inquiries;</li>
              <li>An access-restricted private research dashboard (<code className="text-sm bg-muted px-1 rounded">/peptides/</code>) for the site owner's personal use.</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-serif font-medium text-foreground mb-4">3. Permitted Use</h2>
            <p className="text-muted-foreground leading-relaxed">
              You may access and view the public portions of the Site for personal, non-commercial,
              informational purposes. You may not:
            </p>
            <ul className="list-disc pl-6 text-muted-foreground space-y-2 mt-2">
              <li>Reproduce, distribute, or republish any content from the Site without prior written consent;</li>
              <li>Scrape, crawl, or systematically extract data from the Site by automated means;</li>
              <li>Attempt to access any access-restricted area of the Site without authorization;</li>
              <li>Use the Site for any unlawful purpose or in violation of any applicable law or regulation;</li>
              <li>Attempt to interfere with, disrupt, or gain unauthorized access to the Site's servers, databases, or infrastructure;</li>
              <li>Impersonate any person or entity, or falsely represent your affiliation with any person or entity.</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-serif font-medium text-foreground mb-4">4. Intellectual Property</h2>
            <p className="text-muted-foreground leading-relaxed">
              All content on this Site — including text, images, code, design, field notes, and portfolio
              descriptions — is the original work of Travis Johnson or used with permission, and is
              protected by applicable copyright and intellectual property laws.
            </p>
            <p className="text-muted-foreground leading-relaxed mt-3">
              Nothing in these Terms grants you any license or right to use, copy, or distribute any
              content from the Site except as expressly permitted in Section 3 above.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-serif font-medium text-foreground mb-4">5. Access-Restricted Research Tool</h2>
            <p className="text-muted-foreground leading-relaxed">
              The private research dashboard at <code className="text-sm bg-muted px-1 rounded">/peptides/</code> is intended
              solely for the personal research use of the site owner. Access is governed by a private
              access key. If you have been granted access:
            </p>
            <ul className="list-disc pl-6 text-muted-foreground space-y-2 mt-2">
              <li>You may not share, distribute, or disclose your access key to any third party;</li>
              <li>You may not use the tool for any commercial purpose;</li>
              <li>All research summaries, marketing briefs, and intelligence outputs are generated for
                  informational and research purposes only and do not constitute medical, legal, regulatory,
                  or financial advice;</li>
              <li>Access may be revoked at any time at the sole discretion of the site owner.</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-serif font-medium text-foreground mb-4">6. Third-Party API Usage</h2>
            <p className="text-muted-foreground leading-relaxed">
              The Site integrates with several third-party APIs, including the Meta Ad Library API, to
              retrieve publicly available data for private research purposes. This integration is governed
              by Meta's Platform Terms and Ad Library API Terms of Service. Data retrieved from Meta's
              Ad Library:
            </p>
            <ul className="list-disc pl-6 text-muted-foreground space-y-1 mt-2">
              <li>Is accessed in read-only mode;</li>
              <li>Is used solely for private, non-commercial research by the site owner;</li>
              <li>Is not redistributed, published, or provided to any third party;</li>
              <li>Is not used to build consumer profiles or for targeted advertising.</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-serif font-medium text-foreground mb-4">7. Disclaimers</h2>
            <p className="text-muted-foreground leading-relaxed">
              <strong className="text-foreground">No medical advice.</strong> Content on this Site, including any research summaries, is provided for
              informational purposes only. Nothing on this Site constitutes medical advice, diagnosis, or
              treatment. Consult a qualified healthcare provider before making any health-related decisions.
            </p>
            <p className="text-muted-foreground leading-relaxed mt-3">
              <strong className="text-foreground">No warranty.</strong> The Site is provided "as is" without warranties of any kind, either express
              or implied, including but not limited to implied warranties of merchantability, fitness for a
              particular purpose, or non-infringement. I do not warrant that the Site will be uninterrupted,
              error-free, or free of viruses or other harmful components.
            </p>
            <p className="text-muted-foreground leading-relaxed mt-3">
              <strong className="text-foreground">Accuracy.</strong> While I strive for accuracy, I make no representations or warranties about the
              completeness, accuracy, or timeliness of any content on the Site, including AI-generated
              research summaries. Content may contain errors and should not be relied upon as definitive.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-serif font-medium text-foreground mb-4">8. Limitation of Liability</h2>
            <p className="text-muted-foreground leading-relaxed">
              To the fullest extent permitted by law, Travis Johnson shall not be liable for any indirect,
              incidental, special, consequential, or punitive damages arising out of or related to your use
              of (or inability to use) the Site or any content on it, even if advised of the possibility
              of such damages. My total liability for any claim arising from these Terms or the Site shall
              not exceed one dollar ($1.00 USD).
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-serif font-medium text-foreground mb-4">9. External Links</h2>
            <p className="text-muted-foreground leading-relaxed">
              The Site may contain links to external websites. These links are provided for convenience
              only. I do not endorse, control, or take responsibility for the content or privacy practices
              of any linked site. Visiting external links is at your own risk.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-serif font-medium text-foreground mb-4">10. Privacy</h2>
            <p className="text-muted-foreground leading-relaxed">
              Use of the Site is also governed by our{" "}
              <Link href="/privacy" className="text-foreground hover:underline font-medium">Privacy Policy</Link>,
              which is incorporated into these Terms by reference.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-serif font-medium text-foreground mb-4">11. Governing Law</h2>
            <p className="text-muted-foreground leading-relaxed">
              These Terms are governed by the laws of the State of California, without regard to its
              conflict of law provisions. Any disputes arising under these Terms shall be subject to the
              exclusive jurisdiction of the courts located in San Diego County, California.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-serif font-medium text-foreground mb-4">12. Changes to These Terms</h2>
            <p className="text-muted-foreground leading-relaxed">
              I reserve the right to modify these Terms at any time. Changes will be posted on this page
              with an updated "Last updated" date. Continued use of the Site after any changes constitutes
              acceptance of the revised Terms.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-serif font-medium text-foreground mb-4">13. Contact</h2>
            <p className="text-muted-foreground leading-relaxed">
              Questions about these Terms may be directed to:
            </p>
            <div className="mt-3 p-4 bg-muted rounded-lg text-muted-foreground text-sm space-y-1">
              <p><strong className="text-foreground">Travis Johnson</strong></p>
              <p>{site.location}</p>
              <p>
                Email: <a href={`mailto:${site.email}`} className="text-foreground hover:underline">{site.email}</a>
              </p>
              <p>
                Web: <Link href="/contact" className="text-foreground hover:underline">travisjohnson.xyz/contact</Link>
              </p>
            </div>
          </section>

          <p className="text-sm pt-8 border-t border-border text-muted-foreground">
            Last updated: {LAST_UPDATED} · <Link href="/privacy" className="text-foreground hover:underline">Privacy Policy</Link>
          </p>
        </div>
      </div>
    </Layout>
  );
}
